/*
 * SwipeCards — inline chat cards for the in-chat advisor-matching flow
 * (screens/swipe/chatSwipeFlow.js drives the steps, ChatScreen.js renders
 * whichever card the current step returns via <SwipeCard>).
 *
 * Every card is self-contained: once answered it locally collapses to a small
 * read-only summary (same pattern as ChatScreen's GoalConfirmCard) so old cards
 * left in the transcript can't be re-clicked and don't double-fire onAnswer.
 * No modal/overlay — everything renders inline under the bot bubble that owns it,
 * per the approved "you never leave chat" design.
 *
 * Unlike the standalone bh-flow screens (always light by design, see
 * SwipeScreen.js), these cards live inside the real chat surface, which does
 * switch between the light/green and dark/purple themes (App.js `theme` state,
 * constants/themes.js). So every color here uses the same theme-reactive tokens
 * chat itself uses (--bg-card/--text-dark/--text-light/--border-color/--td-green)
 * instead of the fixed --bh-* tokens the bh-flow screens use — otherwise these
 * cards render as a fixed-light box inside a dark-themed conversation.
 */
import React, { useState, useEffect } from 'react';
import { MdCheckCircle, MdEvent, MdSend } from 'react-icons/md';
import { buildAppointmentIcs, downloadIcs, googleCalendarUrl } from '../utils/ics';
import { previewAction, executeAction, queueAgentEMT } from '../services/backendAPI';

const initials = (name = '') =>
  name.trim().split(/\s+/).map((w) => w[0] || '').join('').slice(0, 2).toUpperCase();

// Small, theme-reactive style primitives shared by every card. Buttons are
// sized to sit comfortably inside a chat bubble (~40px tall), matching the
// advisor carousel's "Select" button rather than the full-size .bh-btn-cta
// used on the standalone (always-light, full-page) Swipe screen.
const S = {
  card: {
    marginTop: 8, background: 'var(--bg-card)', border: '1px solid var(--border-color)',
    borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-sm)', padding: 'var(--space-sm)',
    maxWidth: 340, display: 'flex', flexDirection: 'column', gap: 'var(--space-2xs)',
  },
  sectionLabel: { margin: 0, font: 'var(--bh-caption)', fontWeight: 700, color: 'var(--text-light)', textTransform: 'uppercase' },
  answered: { display: 'flex', alignItems: 'center', gap: 6, font: 'var(--bh-body-sm)', color: 'var(--text-dark)', fontWeight: 600 },
  rowBtn: (selected) => ({
    width: '100%', textAlign: 'left', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    gap: 10, padding: '10px 12px', borderRadius: 'var(--radius-md)', cursor: 'pointer',
    border: `2px solid ${selected ? 'var(--td-green)' : 'var(--border-color)'}`,
    background: selected ? 'var(--bg-light-gray)' : 'var(--bg-card)',
  }),
  rowTitle: { font: 'var(--bh-body-sm)', fontWeight: 600, color: 'var(--text-dark)' },
  rowSub: { font: 'var(--bh-caption)', color: 'var(--text-light)' },
  ctaBtn: (disabled) => ({
    minHeight: 40, padding: '8px 16px', font: 'var(--bh-body-sm)', fontWeight: 700,
    borderRadius: 'var(--radius-md)', cursor: disabled ? 'not-allowed' : 'pointer', border: 'none',
    background: 'var(--td-green)', color: '#fff', opacity: disabled ? 0.5 : 1,
  }),
  outlineBtn: {
    minHeight: 40, padding: '8px 16px', font: 'var(--bh-body-sm)', fontWeight: 700,
    borderRadius: 'var(--radius-md)', cursor: 'pointer', background: 'transparent',
    color: 'var(--text-dark)', border: '1.5px solid var(--border-color)',
  },
  fieldLabel: { display: 'block', font: 'var(--bh-caption)', fontWeight: 600, color: 'var(--text-dark)', textTransform: 'capitalize', marginBottom: 4 },
  fieldInput: {
    width: '100%', boxSizing: 'border-box', background: 'var(--bg-light-gray)', border: '2px solid var(--border-color)',
    borderRadius: 'var(--radius-md)', padding: '10px 12px', font: 'var(--bh-body-sm)', color: 'var(--text-dark)',
  },
};

function chip(bg, color) {
  return {
    display: 'inline-flex', alignItems: 'center', background: bg, color,
    font: 'var(--bh-caption)', fontWeight: 700, padding: '3px 9px', borderRadius: 'var(--radius-full)',
  };
}

function ReviewRow({ label, value }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
      <span style={{ font: 'var(--bh-caption)', color: 'var(--text-light)', flexShrink: 0 }}>{label}</span>
      <span style={{ font: 'var(--bh-body-sm)', fontWeight: 600, color: 'var(--text-dark)', textAlign: 'right' }}>{value}</span>
    </div>
  );
}

// ─── Contact confirm ──────────────────────────────────────────────────────

function ContactConfirmCard({ contact, onAnswer }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(contact);
  const [done, setDone] = useState(false);

  if (done) {
    return (
      <div style={S.card}>
        <div style={S.answered}><MdCheckCircle size={16} color="var(--td-green)" /> Contact confirmed — {form.name}</div>
      </div>
    );
  }

  if (editing) {
    return (
      <div style={S.card}>
        {['phone', 'email'].map((key) => (
          <div key={key}>
            <label htmlFor={`swipe-chat-${key}`} style={S.fieldLabel}>{key}</label>
            <input
              id={`swipe-chat-${key}`}
              style={S.fieldInput}
              type={key === 'email' ? 'email' : 'tel'}
              value={form[key]}
              onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
            />
          </div>
        ))}
        <button style={S.ctaBtn(false)} onClick={() => { setEditing(false); setDone(true); onAnswer(form, 'Updated my contact details'); }}>Save</button>
      </div>
    );
  }

  return (
    <div style={S.card}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <ReviewRow label="Name" value={form.name} />
        <ReviewRow label="Phone" value={form.phone} />
        <ReviewRow label="Email" value={form.email} />
      </div>
      <button style={S.ctaBtn(false)} onClick={() => { setDone(true); onAnswer(form, "Yes, that's me"); }}>Yes, {"that's"} me</button>
      <button style={S.outlineBtn} onClick={() => setEditing(true)}>{"Something's"} off — let me fix it</button>
    </div>
  );
}

// ─── Generic single-choice list ───────────────────────────────────────────

function ChoiceCard({ options, skippable, onAnswer }) {
  const [picked, setPicked] = useState(null);

  const pick = (opt) => {
    if (picked) return;
    setPicked(opt);
    onAnswer(opt.id, opt.label);
  };

  if (picked) {
    return (
      <div style={S.card}>
        <div style={S.answered}><MdCheckCircle size={16} color="var(--td-green)" /> {picked.label ?? 'Skipped'}</div>
      </div>
    );
  }

  return (
    <div style={S.card}>
      {options.map((opt) => (
        <button key={opt.id} type="button" style={S.rowBtn(false)} onClick={() => pick(opt)}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
            {opt.icon && <span style={{ fontSize: '1.1rem', flexShrink: 0 }}>{opt.icon}</span>}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2, textAlign: 'left' }}>
              <span style={S.rowTitle}>{opt.label}</span>
              {opt.sub && <span style={S.rowSub}>{opt.sub}</span>}
            </div>
          </div>
        </button>
      ))}
      {skippable && (
        <button
          type="button"
          onClick={() => pick({ id: null, label: 'Skipped' })}
          style={{ background: 'transparent', border: 'none', color: 'var(--text-light)', font: 'var(--bh-caption)', fontWeight: 600, cursor: 'pointer', padding: 4, alignSelf: 'flex-start' }}
        >
          Skip this
        </button>
      )}
    </div>
  );
}

// ─── Advisor carousel ──────────────────────────────────────────────────────

function AdvisorTile({ advisor, onSelect }) {
  return (
    <div style={{
      flex: '0 0 240px', scrollSnapAlign: 'start', background: 'var(--bg-card)',
      border: '1px solid var(--border-color)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-sm)',
      padding: 'var(--space-sm)', display: 'flex', flexDirection: 'column', gap: 6,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {advisor.photo
          ? <img src={advisor.photo} alt={advisor.name} className="bh-avatar size-md" style={{ objectFit: 'cover' }} />
          : <div className="bh-avatar size-md">{initials(advisor.name)}</div>}
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ font: 'var(--bh-body-sm)', fontWeight: 700, color: 'var(--text-dark)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{advisor.name}</div>
          <div style={{ font: 'var(--bh-caption)', color: 'var(--text-light)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{advisor.title}</div>
        </div>
        <span style={chip('var(--td-green)', '#fff')}>{advisor.matchScore}%</span>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
        {advisor.certs.map((c) => <span key={c} style={chip('var(--bg-light-gray)', 'var(--text-dark)')}>{c}</span>)}
      </div>
      <p style={{ margin: 0, font: 'var(--bh-caption)', color: 'var(--text-light)', lineHeight: 1.4 }}>{advisor.whyMatch}</p>
      <button style={S.ctaBtn(false)} onClick={() => onSelect(advisor)}>Select</button>
    </div>
  );
}

function AdvisorCarouselCard({ advisors, onAnswer }) {
  const [picked, setPicked] = useState(null);

  const pick = (advisor) => {
    if (picked) return;
    setPicked(advisor);
    onAnswer(advisor, `I'll go with ${advisor.name}`);
  };

  if (picked) {
    return (
      <div style={S.card}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {picked.photo
            ? <img src={picked.photo} alt={picked.name} className="bh-avatar size-md" style={{ objectFit: 'cover' }} />
            : <div className="bh-avatar size-md">{initials(picked.name)}</div>}
          <div style={S.answered}><MdCheckCircle size={16} color="var(--td-green)" /> Selected {picked.name}</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ marginTop: 8, display: 'flex', gap: 'var(--space-2xs)', overflowX: 'auto', scrollSnapType: 'x mandatory', paddingBottom: 4, maxWidth: '100%' }}>
      {advisors.map((a) => <AdvisorTile key={a.id} advisor={a} onSelect={pick} />)}
    </div>
  );
}

// ─── Scheduler (slot + channel, all inline) ────────────────────────────────

function SchedulerCard({ advisor, slots, channels, onAnswer }) {
  const [slotId, setSlotId] = useState(null);
  const [channelId, setChannelId] = useState(null);
  const [done, setDone] = useState(null); // { slot, channel } once confirmed

  const confirm = () => {
    const slot = slots.find((s) => s.id === slotId);
    const channel = channels.find((c) => c.id === channelId);
    if (!slot || !channel) return;
    setDone({ slot, channel });
    onAnswer({ slot, channel }, `${slot.label} · ${channel.label}`);
  };

  if (done) {
    return (
      <div style={S.card}>
        <div style={S.answered}><MdCheckCircle size={16} color="var(--td-green)" /> Requested {done.slot.label} · {done.channel.label}</div>
      </div>
    );
  }

  return (
    <div style={S.card}>
      <p style={S.sectionLabel}>Pick a time</p>
      {slots.map((s) => (
        <button key={s.id} type="button" style={S.rowBtn(slotId === s.id)} onClick={() => setSlotId(s.id)}>
          <span style={S.rowTitle}>{s.label}</span>
          {slotId === s.id && <MdCheckCircle size={18} color="var(--td-green)" />}
        </button>
      ))}

      <p style={{ ...S.sectionLabel, marginTop: 4 }}>How would you like to meet {advisor.name.split(' ')[0]}?</p>
      <div style={{ display: 'flex', gap: 6 }}>
        {channels.map((c) => {
          const selected = channelId === c.id;
          return (
            <button
              key={c.id}
              type="button"
              onClick={() => setChannelId(c.id)}
              style={{
                flex: 1, padding: '8px 6px', borderRadius: 'var(--radius-full)', cursor: 'pointer',
                border: `2px solid ${selected ? 'var(--td-green)' : 'var(--border-color)'}`,
                background: selected ? 'var(--td-green)' : 'var(--bg-card)',
                color: selected ? '#fff' : 'var(--text-dark)',
                font: 'var(--bh-caption)', fontWeight: 700,
              }}
            >
              {c.icon} {c.label}
            </button>
          );
        })}
      </div>

      <button style={S.ctaBtn(!slotId || !channelId)} disabled={!slotId || !channelId} onClick={confirm}>Confirm appointment</button>
    </div>
  );
}

// ─── Confirmation (terminal) ────────────────────────────────────────────────

function ConfirmationCard({ advisor, slot, channel }) {
  const [ref] = useState(() => `CAN${Math.floor(100000 + Math.random() * 900000)}`);
  const hasRealTime = slot?.start instanceof Date && !Number.isNaN(slot.start.getTime());
  const title = `Appointment with ${advisor.name} — TD`;
  const description = `TD advisor appointment with ${advisor.name} (${advisor.title}) via ${channel.label}. Reference #${ref}.`;
  const location = channel.id === 'branch' ? advisor.address : channel.label;

  const handleDownloadIcs = () => {
    const ics = buildAppointmentIcs({ uid: `${ref}@iva.td`, title, description, location, start: slot.start, end: slot.end });
    downloadIcs(`td-advisor-appointment-${ref}.ics`, ics);
  };

  return (
    <div style={S.card}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ width: 40, height: 40, borderRadius: 'var(--radius-full)', background: 'var(--td-green)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <MdCheckCircle size={22} />
        </div>
        <div>
          <p style={{ margin: 0, fontWeight: 700, font: 'var(--bh-body)', color: 'var(--text-dark)' }}>{"You're"} booked!</p>
          <p style={{ margin: 0, font: 'var(--bh-caption)', color: 'var(--text-light)' }}>with {advisor.name} · {advisor.title}</p>
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <ReviewRow label="When" value={slot.label} />
        <ReviewRow label="How" value={`${channel.icon} ${channel.label}`} />
        <ReviewRow label="Reference" value={ref} />
      </div>
      {hasRealTime && (
        <div style={{ display: 'flex', gap: 6 }}>
          <button style={{ ...S.outlineBtn, flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }} onClick={handleDownloadIcs}>
            <MdEvent size={16} /> Download .ics
          </button>
          <a
            href={googleCalendarUrl({ title, description, location, start: slot.start, end: slot.end })}
            target="_blank"
            rel="noreferrer"
            style={{ ...S.outlineBtn, flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, textDecoration: 'none' }}
          >
            <MdEvent size={16} /> Add to Google
          </a>
        </div>
      )}
    </div>
  );
}

// ─── EMT Triage ─────────────────────────────────────────────────────────────

export function EMTTriageCard({ onAnswer }) {
  const [picked, setPicked] = useState(null);

  const pick = (id, label) => {
    if (picked) return;
    setPicked({ id, label });
    onAnswer(id, label);
  };

  if (picked) {
    return (
      <div style={S.card}>
        <div style={S.answered}><MdCheckCircle size={16} color="var(--td-green)" /> {picked.label}</div>
      </div>
    );
  }

  const options = [
    { id: 'self', icon: '✅', label: 'Do it myself', sub: 'Walk me through it step by step' },
    { id: 'agent', icon: '🤖', label: 'Have an agent handle it', sub: "I'll review and approve before it sends" },
    { id: 'advisor', icon: '👤', label: 'Speak with an advisor', sub: 'Connect me with a TD specialist' },
  ];

  return (
    <div style={S.card}>
      {options.map((opt) => (
        <button key={opt.id} type="button" style={S.rowBtn(false)} onClick={() => pick(opt.id, opt.label)}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
            <span style={{ fontSize: '1.1rem', flexShrink: 0 }}>{opt.icon}</span>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2, textAlign: 'left' }}>
              <span style={S.rowTitle}>{opt.label}</span>
              {opt.sub && <span style={S.rowSub}>{opt.sub}</span>}
            </div>
          </div>
        </button>
      ))}
    </div>
  );
}

// ─── EMT Free-Text Input ─────────────────────────────────────────────────────

const EMT_EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function EMTFreeTextCard({ label, placeholder, inputType, prefix, validate, skippable, cta, onAnswer }) {
  const [value, setValue] = useState('');
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  if (done) {
    return (
      <div style={S.card}>
        <div style={S.answered}>
          <MdCheckCircle size={16} color="var(--td-green)" />
          {value ? (prefix ? `${prefix}${value}` : value) : 'Skipped'}
        </div>
      </div>
    );
  }

  const submit = (skipValue) => {
    if (skipValue !== undefined) {
      setDone(true);
      onAnswer(skipValue, 'Skipped');
      return;
    }
    const trimmed = value.trim();
    if (!trimmed && !skippable) return;
    if (validate === 'email' && !EMT_EMAIL_RE.test(trimmed)) {
      setError('Please enter a valid email address.');
      return;
    }
    if (validate === 'positive-number') {
      const n = parseFloat(trimmed);
      if (!(n > 0)) {
        setError('Please enter a positive amount.');
        return;
      }
    }
    setDone(true);
    onAnswer(trimmed, trimmed);
  };

  return (
    <div style={S.card}>
      <div>
        <label style={S.fieldLabel}>{label}</label>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {prefix && <span style={{ fontWeight: 700, color: 'var(--text-dark)', flexShrink: 0 }}>{prefix}</span>}
          <input
            style={{ ...S.fieldInput, flex: 1 }}
            type={inputType || 'text'}
            placeholder={placeholder}
            value={value}
            onChange={(e) => { setValue(e.target.value); setError(''); }}
            onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
            autoFocus
          />
        </div>
        {error && <p style={{ margin: '4px 0 0', font: 'var(--bh-caption)', color: 'var(--color-error, #c0392b)' }}>{error}</p>}
      </div>
      <button style={S.ctaBtn(!value.trim() && !skippable)} disabled={!value.trim() && !skippable} onClick={() => submit()}>{cta || 'Next'}</button>
      {skippable && (
        <button type="button" onClick={() => submit('')} style={{ background: 'transparent', border: 'none', color: 'var(--text-light)', font: 'var(--bh-caption)', fontWeight: 600, cursor: 'pointer', padding: 4, alignSelf: 'flex-start' }}>
          Skip — no message
        </button>
      )}
    </div>
  );
}

// ─── EMT Preview / Confirm ───────────────────────────────────────────────────

function EMTPreviewCard({ sessionId, recipientEmail, amount, message, onAnswer }) {
  const [status, setStatus] = useState('idle');
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState('');
  const [answered, setAnswered] = useState(false);

  const params = { recipient: recipientEmail, amount, ...(message ? { message } : {}) };

  const runPreview = async () => {
    setStatus('loading');
    setPreviewError('');
    const res = await previewAction(sessionId, 'send_email_money_transfer', params);
    if (!res.ok) {
      setPreviewError(res.reason || 'Unable to process transfer — please check your balance.');
      setStatus('error');
    } else {
      setPreview(res);
      setStatus('ready');
    }
  };

  useEffect(() => { runPreview(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const confirm = async () => {
    if (answered) return;
    setStatus('sending');
    const res = await executeAction(sessionId, 'send_email_money_transfer', params);
    setAnswered(true);
    if (res.ok) {
      setStatus('sent');
      onAnswer({ confirmed: true, referenceNumber: res.actionId || res.referenceNumber });
    } else {
      setStatus('failed');
      setPreviewError(res.reason || 'Transfer failed — please try again.');
      onAnswer({ confirmed: false });
    }
  };

  const cancel = () => {
    if (answered) return;
    setAnswered(true);
    setStatus('cancelled');
    onAnswer({ confirmed: false });
  };

  if (status === 'loading') {
    return (
      <div style={S.card}>
        <p style={{ margin: 0, font: 'var(--bh-body-sm)', color: 'var(--text-light)' }}>Checking your account…</p>
      </div>
    );
  }

  if (status === 'sending') {
    return (
      <div style={S.card}>
        <p style={{ margin: 0, font: 'var(--bh-body-sm)', color: 'var(--text-light)' }}>Sending transfer…</p>
      </div>
    );
  }

  if (status === 'sent') {
    return (
      <div style={S.card}>
        <div style={S.answered}><MdCheckCircle size={16} color="var(--td-green)" /> Transfer sent!</div>
      </div>
    );
  }

  if (status === 'cancelled') {
    return (
      <div style={S.card}>
        <div style={S.answered}><MdCheckCircle size={16} color="var(--td-green)" /> Cancelled — no money moved</div>
      </div>
    );
  }

  return (
    <div style={S.card}>
      {(status === 'error' || status === 'failed') && previewError && (
        <p style={{ margin: 0, font: 'var(--bh-body-sm)', color: 'var(--color-error, #c0392b)', fontWeight: 600 }}>{previewError}</p>
      )}
      {status === 'ready' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <ReviewRow label="To" value={recipientEmail} />
          <ReviewRow label="Amount" value={`$${Number(amount).toFixed(2)}`} />
          {message && <ReviewRow label="Note" value={message} />}
          {preview?.after != null && <ReviewRow label="Balance after" value={`$${Number(preview.after).toFixed(2)}`} />}
        </div>
      )}
      {status === 'ready' && (
        <button style={S.ctaBtn(false)} onClick={confirm}>
          <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
            <MdSend size={15} /> Confirm & send
          </span>
        </button>
      )}
      {(status === 'ready' || status === 'error') && (
        <button style={S.outlineBtn} onClick={cancel}>Cancel</button>
      )}
    </div>
  );
}

// ─── EMT Success (terminal) ───────────────────────────────────────────────────

function EMTSuccessCard({ recipientEmail, amount, referenceNumber }) {
  return (
    <div style={S.card}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{
          width: 40, height: 40, borderRadius: 'var(--radius-full)',
          background: 'var(--td-green)', color: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}>
          <MdCheckCircle size={22} />
        </div>
        <div>
          <p style={{ margin: 0, fontWeight: 700, font: 'var(--bh-body)', color: 'var(--text-dark)' }}>e-Transfer sent!</p>
          <p style={{ margin: 0, font: 'var(--bh-caption)', color: 'var(--text-light)' }}>{recipientEmail}</p>
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <ReviewRow label="Amount" value={`$${Number(amount).toFixed(2)}`} />
        {referenceNumber && <ReviewRow label="Reference" value={referenceNumber} />}
      </div>
    </div>
  );
}

// ─── Agent EMT Confirm ────────────────────────────────────────────────────────

function EMTAgentConfirmCard({ sessionId, recipientEmail, amount, message, onAnswer }) {
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');
  const [answered, setAnswered] = useState(false);

  const queue = async () => {
    if (answered) return;
    setStatus('queuing');
    setError('');
    const res = await queueAgentEMT(sessionId, { recipientEmail, amount, message });
    setAnswered(true);
    if (res.ok) {
      setStatus('queued');
      onAnswer({ confirmed: true, referenceNumber: res.referenceNumber });
    } else {
      setStatus('idle');
      setError(res.reason || 'Unable to queue transfer — please try again.');
    }
  };

  const cancel = () => {
    if (answered) return;
    setAnswered(true);
    setStatus('cancelled');
    onAnswer({ confirmed: false });
  };

  if (status === 'queuing') {
    return (
      <div style={S.card}>
        <p style={{ margin: 0, font: 'var(--bh-body-sm)', color: 'var(--text-light)' }}>Queueing for agent review…</p>
      </div>
    );
  }

  if (status === 'queued') {
    return (
      <div style={S.card}>
        <div style={S.answered}><MdCheckCircle size={16} color="var(--td-green)" /> Queued for agent review</div>
      </div>
    );
  }

  if (status === 'cancelled') {
    return (
      <div style={S.card}>
        <div style={S.answered}><MdCheckCircle size={16} color="var(--td-green)" /> Cancelled — nothing queued</div>
      </div>
    );
  }

  return (
    <div style={S.card}>
      {error && (
        <p style={{ margin: 0, font: 'var(--bh-body-sm)', color: 'var(--color-error, #c0392b)', fontWeight: 600 }}>{error}</p>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <ReviewRow label="To" value={recipientEmail} />
        <ReviewRow label="Amount" value={`$${Number(amount).toFixed(2)}`} />
        {message && <ReviewRow label="Note" value={message} />}
      </div>
      <button style={S.ctaBtn(false)} onClick={queue}>
        <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
          🤖 Queue for agent
        </span>
      </button>
      <button style={S.outlineBtn} onClick={cancel}>Cancel</button>
    </div>
  );
}

// ─── Agent EMT Queued (terminal) ─────────────────────────────────────────────

function EMTAgentQueuedCard({ recipientEmail, amount, referenceNumber }) {
  return (
    <div style={S.card}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{
          width: 40, height: 40, borderRadius: 'var(--radius-full)',
          background: 'var(--td-green)', color: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          fontSize: '1.2rem',
        }}>🤖</div>
        <div>
          <p style={{ margin: 0, fontWeight: 700, font: 'var(--bh-body)', color: 'var(--text-dark)' }}>Agent will handle it</p>
          <p style={{ margin: 0, font: 'var(--bh-caption)', color: 'var(--text-light)' }}>You'll be notified when it's done</p>
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <ReviewRow label="To" value={recipientEmail} />
        <ReviewRow label="Amount" value={`$${Number(amount).toFixed(2)}`} />
        {referenceNumber && <ReviewRow label="Reference" value={referenceNumber} />}
      </div>
    </div>
  );
}

// ─── Dispatcher ─────────────────────────────────────────────────────────────

export default function SwipeCard({ card, sessionId, onAnswer }) {
  if (!card) return null;
  switch (card.type) {
    case 'contact-confirm':
      return <ContactConfirmCard contact={card.contact} onAnswer={onAnswer} />;
    case 'choice':
      return <ChoiceCard options={card.options} skippable={card.skippable} onAnswer={onAnswer} />;
    case 'advisor-carousel':
      return <AdvisorCarouselCard advisors={card.advisors} onAnswer={onAnswer} />;
    case 'scheduler':
      return <SchedulerCard advisor={card.advisor} slots={card.slots} channels={card.channels} onAnswer={onAnswer} />;
    case 'confirmation':
      return <ConfirmationCard advisor={card.advisor} slot={card.slot} channel={card.channel} />;
    case 'emt-free-text':
      return <EMTFreeTextCard label={card.label} placeholder={card.placeholder} inputType={card.inputType} prefix={card.prefix} validate={card.validate} skippable={card.skippable} cta={card.cta} onAnswer={onAnswer} />;
    case 'emt-preview':
      return <EMTPreviewCard sessionId={sessionId} recipientEmail={card.recipientEmail} amount={card.amount} message={card.message} onAnswer={onAnswer} />;
    case 'emt-success':
      return <EMTSuccessCard recipientEmail={card.recipientEmail} amount={card.amount} referenceNumber={card.referenceNumber} />;
    case 'emt-agent-confirm':
      return <EMTAgentConfirmCard sessionId={sessionId} recipientEmail={card.recipientEmail} amount={card.amount} message={card.message} onAnswer={onAnswer} />;
    case 'emt-agent-queued':
      return <EMTAgentQueuedCard recipientEmail={card.recipientEmail} amount={card.amount} referenceNumber={card.referenceNumber} />;
    default:
      return null;
  }
}
